import {
  XCircle,
  Wallet,
  Ban,
  Send,
  ClipboardCheck,
  RefreshCcw,
} from "lucide-react";
import {
  legalTheme,
  ScrollProgressRail,
  LegalHero,
  SectionNav,
  MobileSectionPills,
  LegalSection,
  ContactSection,
  useActiveSection,
} from "../components/legal/LegalKit";

const sections = [
  {
    id: "cancellation-policy",
    no: 1,
    title: "Cancellation Policy",
    icon: XCircle,
    paragraphs: [
      "You may request cancellation of a project or service engagement any time before work has commenced. Once work is underway, cancellation requests are reviewed on a case-by-case basis and may be subject to charges for work already completed.",
      "Cancellation timelines and any applicable charges may vary depending on the type of service, project scope, or engagement stage. After the agreed cancellation window, cancellation charges may apply.",
      "If UniBcomp needs to cancel an engagement due to unforeseen circumstances, a full refund will be issued for any prepaid amount not yet utilised.",
      "UniBcomp reserves the right to review, accept, or decline cancellation requests in accordance with this policy and applicable laws.",
    ],
  },
  {
    id: "refund-eligibility",
    no: 2,
    title: "Refund Eligibility",
    icon: Wallet,
    intro: "Refunds, credit, or re-work may be offered in the following situations:",
    items: [
      "Service or deliverable not provided as agreed",
      "Technical error resulting in a duplicate or incorrect payment",
      "Project or service not delivered due to reasons attributable to UniBcomp",
      "Cancellation approved under this policy",
    ],
    note: "Refund terms specific to a service or engagement, where mentioned in your project agreement or invoice, will take precedence where applicable.",
  },
  {
    id: "non-refundable",
    no: 3,
    title: "Non-Refundable Situations",
    icon: Ban,
    intro: "Refunds will generally not be provided for:",
    items: [
      "Services already completed or substantially delivered",
      "Projects cancelled after significant work has commenced",
      "Delays caused by incomplete information or lack of cooperation from the client",
      "Change of business requirements after project initiation",
      "Digital deliverables already shared or accepted",
    ],
  },
  {
    id: "how-to-raise",
    no: 4,
    title: "How to Raise a Refund Request",
    icon: Send,
    paragraphs: [
      "Refund requests should be raised within 7 days of the concern arising, along with your invoice or payment reference and a description of the issue, emailed to support@unibcomp.net.",
      "After review and approval, refunds are processed within 7–10 business days, subject to the original payment method and any applicable deductions.",
    ],
  },
  {
    id: "important-conditions",
    no: 5,
    title: "Important Conditions & Eligibility",
    icon: ClipboardCheck,
    items: [
      "A valid invoice, payment reference, or signed agreement is required",
      "The request must relate to work not yet delivered, or a genuine billing or technical error",
      "Engagements already confirmed and commenced cannot be cancelled retroactively",
    ],
  },
  {
    id: "policy-updates",
    no: 6,
    title: "Policy Updates",
    icon: RefreshCcw,
    paragraphs: [
      "UniBcomp may update this Refund & Cancellation Policy periodically. Continued use of our services or website constitutes acceptance of the updated policy.",
    ],
  },
];

const sectionIds = [...sections.map((s) => s.id), "contact-us"];

export default function RefundCancellation() {
  const activeId = useActiveSection(sectionIds);

  return (
    <div className="w-full" style={{ backgroundColor: legalTheme.surface }}>
      <ScrollProgressRail />

      <LegalHero
        eyebrow="Legal"
        title={["Refund &", "Cancellation Policy"]}
        HeroIcon={Wallet}
        meta={
          <>
            <span className="inline-flex items-center gap-2">
              <Wallet size={15} style={{ color: legalTheme.gold }} />
              Last Updated: July 2026
            </span>
            <span className="hidden sm:inline">·</span>
            <span>UniBcomp Networks Private Limited</span>
          </>
        }
        description="Understand how cancellations and refunds are handled at UniBcomp, ensuring a transparent and smooth experience when working with us."
        agreementNote="This policy applies to all payments made for our services. For any questions about cancellations or refunds, please contact our support team at support@unibcomp.net."
      />

      <MobileSectionPills sections={sections} activeId={activeId} />

      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[240px_1fr] gap-16">
          <SectionNav sections={sections} activeId={activeId} />

          <div className="min-w-0 max-w-3xl">
            {sections.map((section) => (
              <LegalSection key={section.id} section={section} />
            ))}

            <ContactSection
              no={7}
              intro="For any questions regarding cancellations or refunds, please contact us:"
              cards={[
                {
                  label: "Cancellations & Refunds",
                  desc: "For any questions regarding this Refund & Cancellation Policy.",
                  email: "support@unibcomp.net",
                },
              ]}
            />

            <div className="mt-8 pt-8 border-t text-sm" style={{ borderColor: legalTheme.border, color: legalTheme.inkMuted }}>
              © 2026 UniBcomp Networks Private Limited. All rights reserved.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}